import { Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from 'sonner'
import { AuthProvider } from './contexts/AuthContext'
import { ThemeProvider } from './contexts/ThemeContext'
import { ProtectedRoute } from './components/ProtectedRoute'
import { Layout } from './components/Layout'
import { InstallPrompt } from './components/InstallPrompt'
import { LoginPage } from './pages/LoginPage'
import { ForgotPasswordPage } from './pages/ForgotPasswordPage'
import { ResetPasswordPage } from './pages/ResetPasswordPage'
import { ForceChangePasswordPage } from './pages/ForceChangePasswordPage'
import { VerifyEmailPage } from './pages/VerifyEmailPage'
import { DashboardPage } from './pages/DashboardPage'
import { EmployeesPage } from './pages/EmployeesPage'
import { RequestsPage } from './pages/RequestsPage'
import { NewRequestPage } from './pages/NewRequestPage'
import { NotificationsPage } from './pages/NotificationsPage'
import UserManualPage from './pages/employee/UserManualPage'
import ManualsPage from './pages/admin/ManualsPage'
import { RulesPage } from './pages/configuration/RulesPage'
import { UsersPage } from './pages/configuration/UsersPage'
import { EmailsPage } from './pages/configuration/EmailsPage'
import { WorkCentersPage } from './pages/configuration/WorkCentersPage'
import { LimitsPage } from './pages/configuration/LimitsPage'
import { PositionsPage } from './pages/configuration/PositionsPage'
import { ShiftsPage } from './pages/configuration/ShiftsPage'
import { ShiftDetailPage } from './pages/configuration/shifts/ShiftDetailPage'
import { ShiftCreatePage } from './pages/configuration/shifts/ShiftCreatePage'

// Employee Detail with Tabs
import {
  EmployeeDetailLayout,
  EmployeeOverviewTab,
  EmployeeRequestsTab,
  EmployeeNotesTab,
  EmployeeNotificationsTab,
} from './pages/employees'

// Request Detail with Tabs
import {
  RequestDetailLayout,
  RequestOverviewTab,
  RequestHistoryTab,
} from './pages/requests'

// Configuration Detail with Tabs
import {
  UserDetailLayout,
  UserOverviewTab,
} from './pages/configuration/users'

import {
  RuleDetailLayout,
  RuleOverviewTab,
} from './pages/configuration/rules'

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <InstallPrompt />
        <Toaster
          position="top-right"
          richColors
          closeButton
          expand={true}
          toastOptions={{
            duration: 5000,
            style: {
              borderRadius: '12px',
              zIndex: 99999,
            },
            classNames: {
              toast: 'shadow-lg',
              error: 'bg-red-50 border-red-200 text-red-800',
            },
          }}
        />
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/change-password" element={
            <ProtectedRoute>
              <ForceChangePasswordPage />
            </ProtectedRoute>
          } />
          <Route path="/verify-email" element={
            <ProtectedRoute>
              <VerifyEmailPage />
            </ProtectedRoute>
          } />

          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <DashboardPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/employees"
            element={
              <ProtectedRoute>
                <Layout>
                  <EmployeesPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/employees/:id"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <Layout>
                  <EmployeeDetailLayout />
                </Layout>
              </ProtectedRoute>
            }
          >
            <Route index element={<EmployeeOverviewTab />} />
            <Route path="requests" element={<EmployeeRequestsTab />} />
            <Route path="notes" element={<EmployeeNotesTab />} />
            <Route path="notifications" element={<EmployeeNotificationsTab />} />
          </Route>
          <Route
            path="/requests"
            element={
              <ProtectedRoute>
                <Layout>
                  <RequestsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/requests/new"
            element={
              <ProtectedRoute>
                <Layout>
                  <NewRequestPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <Layout>
                  <NotificationsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/requests/:id"
            element={
              <ProtectedRoute>
                <Layout>
                  <RequestDetailLayout />
                </Layout>
              </ProtectedRoute>
            }
          >
            <Route index element={<RequestOverviewTab />} />
            <Route path="history" element={<RequestHistoryTab />} />
          </Route>
          {/* Configuration Routes */}
          <Route
            path="/config"
            element={<Navigate to="/configuration/rules" replace />}
          />
          <Route
            path="/configuration"
            element={<Navigate to="/configuration/rules" replace />}
          />
          <Route
            path="/configuration/rules"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <Layout>
                  <RulesPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/configuration/rules/:id"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <Layout>
                  <RuleDetailLayout />
                </Layout>
              </ProtectedRoute>
            }
          >
            <Route index element={<RuleOverviewTab />} />
          </Route>
          <Route
            path="/configuration/users"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <Layout>
                  <UsersPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/configuration/users/:id"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <Layout>
                  <UserDetailLayout />
                </Layout>
              </ProtectedRoute>
            }
          >
            <Route index element={<UserOverviewTab />} />
          </Route>
          <Route
            path="/configuration/emails"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <Layout>
                  <EmailsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/configuration/work-centers"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <Layout>
                  <WorkCentersPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/configuration/limits"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <Layout>
                  <LimitsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/configuration/positions"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <Layout>
                  <PositionsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/configuration/shifts"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <Layout>
                  <ShiftsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/configuration/shifts/new"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <Layout>
                  <ShiftCreatePage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/configuration/shifts/:id"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <Layout>
                  <ShiftDetailPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/configuration/manuals"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <Layout>
                  <ManualsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/manual"
            element={
              <ProtectedRoute>
                <Layout>
                  <UserManualPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </AuthProvider>
    </ThemeProvider>
  )
}

function NotFound() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-dark-bg">
      <div className="text-center">
        <h1 className="text-8xl font-bold text-gray-200 dark:text-gray-700">404</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-4 text-lg">Página no encontrada</p>
        <a
          href="/dashboard"
          className="btn btn-primary inline-block mt-6"
        >
          Volver al inicio
        </a>
      </div>
    </div>
  )
}

export default App
